import React, { useEffect, useMemo, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { FileText, Pencil, Settings2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { UnifiedTable, type UnifiedTableColumn } from '@/components/ui/unifiedTable/UnifiedTable';
import { useUser } from '@/store/useUser';
import { getCellValue, mapDbTypeToDataType, toDraftValueForEditor } from '@/pages/candidates/utils/candidateProgramTools';
import {
  ProgressReportFields,
  PROGRESS_REPORT_REQUIRED_FIELDS,
} from '@/pages/candidates/programs/progress-report/ProgressReportFields';
import {
  ProgressReportViewerPicker,
  type ProgressReportViewer,
} from '@/pages/candidates/programs/progress-report/ProgressReportViewerPicker';
import { ProgressReportPreview } from '@/pages/candidates/programs/progress-report/ProgressReportPreview';
import { TabHeader } from '../shared/components/TabHeader';

const COLUMN_SETTING_PREFIX = 'PROGRESS_REPORT_COLUMN:';
const DEFAULT_COLUMNS = ['date', 'court_date', 'appearance', 'case_number', 'icms_provider_name', 'completed', 'amended'];
const EXCLUDED_COLUMNS = new Set(['participant_id', 'odrparticipant', 'created_by', 'updated_by']);

const PROGRESS_REPORT_SCHEMA = gql`
  query ParticipantProgressReportSchema {
    participantProgressReportSchema {
      fields
      fieldMeta { name label writable dbType maxLength }
    }
  }
`;

const PROGRESS_REPORTS_FOR_PARTICIPANT = gql`
  query ProgressReportsForParticipant($identifier: Int!) {
    progressReportsForParticipant(identifier: $identifier, page: 1, pageSize: 500, sortField: "date", sortDirection: "DESC") {
      fields
      rows { id cells { key value } }
      totalRows
    }
  }
`;

const PROGRESS_REPORT_DEFAULTS = gql`
  query ParticipantProgressReportDefaults($participantId: Int, $cid: Int) {
    progressReportParticipantByCid(participantId: $participantId, cid: $cid) {
      cells { key value }
    }
  }
`;

const CREATE_PARTICIPANT_PROGRESS_REPORT = gql`
  mutation CreateParticipantProgressReport($participantId: Int!, $odrparticipant: Int!, $cid: Int, $values: [CandidateInputFieldInput!]) {
    createParticipantProgressReport(participantId: $participantId, odrparticipant: $odrparticipant, cid: $cid, values: $values) {
      success id
    }
  }
`;

const SEARCH_VIEWERS = gql`
  query ParticipantProgressReportViewerSearch($search: String!) {
    searchProgressReportViewers(search: $search) { id realname username }
  }
`;

const SYNC_VIEWERS = gql`
  mutation ParticipantProgressReportViewerSync($reportId: Int!, $viewerIds: [Int!]!) {
    syncProgressReportViewers(reportId: $reportId, viewerIds: $viewerIds) { success }
  }
`;

const PROGRESS_REPORT_VIEWERS = gql`
  query ParticipantProgressReportViewers($reportId: Int!) {
    progressReportViewers(reportId: $reportId) { id realname username }
  }
`;

const UPDATE_PARTICIPANT_PROGRESS_REPORT = gql`
  mutation UpdateParticipantProgressReport($identifier: Int!, $id: Int!, $values: [CandidateInputFieldInput!]) {
    updateParticipantProgressReport(identifier: $identifier, id: $id, values: $values) { success id }
  }
`;

type CandidateCell = { key: string; value: unknown };
type ReportRow = { id: number; cells: CandidateCell[] };
type FieldMeta = { name: string; label?: string; writable?: boolean; dbType?: string | null };

type ProgressReportsTabProps = {
  participantId: number;
  odrparticipant: number;
  cid?: number | null;
  clientName?: string;
};

const toLocalDateTime = (): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 16);
};

const confirmProgressReportSubmission = (values: Record<string, string>): boolean => {
  const courtDate = String(values.court_date || '').trim() || 'the scheduled court date';
  const condition = String(values.client_current_condition || '').trim();
  const appearance = condition === 'not_well' || condition === 'graduation_encouragement'
    ? 'appearance'
    : (String(values.appearance || '').trim() || (condition === 'well' ? 'non_appearance' : ''));
  if (appearance === 'appearance' || appearance === 'virtual_appearance') {
    return window.confirm(`The information within the progress report is accurate as of the date of submission. Both the client and case manager are expected to be present for court by 8:30 am on ${courtDate}. Continue?`);
  }
  return window.confirm(`The information in this progress report is accurate as of the date of submission. This matter is scheduled for non-appearance on ${courtDate}; no in-person appearance is required unless otherwise ordered by the Court. Continue?`);
};

export const ProgressReportsTab = ({ participantId, odrparticipant, cid, clientName }: ProgressReportsTabProps) => {
  const apolloClient = useApolloClient();
  const { user, updateSystemSettingsFavorites } = useUser();
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [viewers, setViewers] = useState<ProgressReportViewer[]>([]);
  const [viewerSearch, setViewerSearch] = useState('');
  const [viewerResults, setViewerResults] = useState<ProgressReportViewer[]>([]);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_COLUMNS);
  const [pendingColumns, setPendingColumns] = useState<string[]>(DEFAULT_COLUMNS);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewValues, setPreviewValues] = useState<Record<string, string>>({});
  const [lockedParticipantId, setLockedParticipantId] = useState(participantId);
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editRow, setEditRow] = useState<ReportRow | null>(null);
  const [editDraft, setEditDraft] = useState<Record<string, string>>({});

  const { data: schemaData } = useQuery(PROGRESS_REPORT_SCHEMA);
  const { data, loading, error, refetch } = useQuery(PROGRESS_REPORTS_FOR_PARTICIPANT, {
    variables: { identifier: odrparticipant || participantId },
    skip: !odrparticipant,
  });
  const [createReport] = useMutation(CREATE_PARTICIPANT_PROGRESS_REPORT);
  const [syncViewers] = useMutation(SYNC_VIEWERS);
  const [updateReport] = useMutation(UPDATE_PARTICIPANT_PROGRESS_REPORT);

  const fields: string[] = Array.isArray(schemaData?.participantProgressReportSchema?.fields) ? schemaData.participantProgressReportSchema.fields : [];
  const fieldMeta: FieldMeta[] = Array.isArray(schemaData?.participantProgressReportSchema?.fieldMeta) ? schemaData.participantProgressReportSchema.fieldMeta : [];
  const rows: ReportRow[] = Array.isArray(data?.progressReportsForParticipant?.rows) ? data.progressReportsForParticipant.rows : [];
  const labelsByField = useMemo(() => fieldMeta.reduce<Record<string, string>>((labels, field) => {
    labels[field.name] = field.label || field.name;
    return labels;
  }, {}), [fieldMeta]);
  const schemaWritableByField = useMemo(() => fieldMeta.reduce<Record<string, boolean>>((writable, field) => {
    writable[field.name] = !!field.writable;
    return writable;
  }, {}), [fieldMeta]);
  const writableByField = useMemo(() => Object.fromEntries(
    Object.entries(schemaWritableByField).map(([field, writable]) => [
      field,
      writable && !['participant_id', 'odrparticipant', 'cid', 'client_name', 'appearance'].includes(field),
    ]),
  ), [schemaWritableByField]);
  const configurableFields = useMemo(
    () => fields.filter((field) => !EXCLUDED_COLUMNS.has(field) && field !== 'id'),
    [fields],
  );

  useEffect(() => {
    const saved = (user?.settings?.systemSettingsFavorites || [])
      .filter((value: string) => value.startsWith(COLUMN_SETTING_PREFIX))
      .map((value: string) => value.slice(COLUMN_SETTING_PREFIX.length))
      .filter((field: string) => configurableFields.includes(field));
    const nextColumns = saved.length > 0 ? saved : DEFAULT_COLUMNS.filter((field) => configurableFields.includes(field));
    setVisibleColumns(nextColumns);
    setPendingColumns(nextColumns);
  }, [configurableFields, user?.settings?.systemSettingsFavorites]);

  useEffect(() => {
    if ((!createOpen && !editOpen) || viewerSearch.trim().length < 2) {
      setViewerResults([]);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void apolloClient.query({ query: SEARCH_VIEWERS, variables: { search: viewerSearch.trim() }, fetchPolicy: 'network-only' })
        .then((response) => {
          if (!cancelled) {
            setViewerResults(Array.isArray(response.data?.searchProgressReportViewers) ? response.data.searchProgressReportViewers : []);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setViewerResults([]);
          }
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [apolloClient, createOpen, editOpen, viewerSearch]);

  const resetCreate = () => {
    setDraft({});
    setViewers([]);
    setViewerSearch('');
    setViewerResults([]);
    setLockedParticipantId(participantId);
  };

  const openCreate = async () => {
    resetCreate();
    setCreateLoading(true);
    setCreateOpen(true);
    try {
      const response = await apolloClient.query({
        query: PROGRESS_REPORT_DEFAULTS,
        variables: { participantId: participantId || null, cid: participantId ? null : (cid || null) },
        fetchPolicy: 'network-only',
      });
      const nextDraft: Record<string, string> = { date: toLocalDateTime() };
      for (const cell of response.data?.progressReportParticipantByCid?.cells || []) {
        const meta = fieldMeta.find((field) => field.name === cell.key);
        nextDraft[cell.key] = toDraftValueForEditor(String(cell.value || ''), mapDbTypeToDataType(meta?.dbType, cell.key, meta?.label));
      }
      nextDraft.participant_id = nextDraft.participant_id || String(participantId);
      nextDraft.odrparticipant = String(odrparticipant);
      nextDraft.cid = cid ? String(cid) : nextDraft.cid || '';
      nextDraft.client_name = clientName || nextDraft.client_name || '';
      setLockedParticipantId(Number(nextDraft.participant_id || participantId));
      setDraft(nextDraft);
    } catch (loadError: any) {
      setDraft({
        participant_id: String(participantId),
        odrparticipant: String(odrparticipant),
        cid: cid ? String(cid) : '',
        client_name: clientName || '',
        date: toLocalDateTime(),
      });
      toast.error(loadError?.message || 'Some progress report defaults could not be loaded.');
    } finally {
      setCreateLoading(false);
    }
  };

  const addViewer = (viewer: ProgressReportViewer) => {
    setViewers((current) => current.some((item) => item.id === viewer.id) ? current : [...current, viewer]);
    setViewerSearch('');
    setViewerResults([]);
  };

  const openEdit = async (row: ReportRow) => {
    const values = row.cells.reduce<Record<string, string>>((record, cell) => {
      const meta = fieldMeta.find((field) => field.name === cell.key);
      record[cell.key] = toDraftValueForEditor(String(cell.value ?? ''), mapDbTypeToDataType(meta?.dbType, cell.key, meta?.label));
      return record;
    }, {});
    setEditRow(row);
    setEditDraft(values);
    setViewers([]);
    setViewerSearch('');
    setViewerResults([]);
    setEditOpen(true);
    setEditLoading(true);
    try {
      const response = await apolloClient.query({ query: PROGRESS_REPORT_VIEWERS, variables: { reportId: row.id }, fetchPolicy: 'network-only' });
      setViewers(Array.isArray(response.data?.progressReportViewers) ? response.data.progressReportViewers : []);
    } catch (viewerError: any) {
      toast.error(viewerError?.message || 'Progress report viewers could not be loaded.');
    } finally {
      setEditLoading(false);
    }
  };

  const submitEdit = async () => {
    if (!editRow) return;
    const requiredFields: string[] = editDraft.completed === '1' ? ['client_current_condition'] : [...PROGRESS_REPORT_REQUIRED_FIELDS];
    const missing = requiredFields.filter((field) => !String(editDraft[field] || '').trim());
    if (missing.length > 0) {
      toast.error(`Complete the required fields: ${missing.map((field) => labelsByField[field] || field).join(', ')}`);
      return;
    }
    if (editDraft.completed !== '1' && !confirmProgressReportSubmission(editDraft)) return;

    const saved = Object.fromEntries(editRow.cells.map((cell) => [cell.key, String(cell.value ?? '')]));
    const editableFields = editDraft.completed === '1'
      ? new Set(['client_current_condition', 'appearance'])
      : new Set(fields.filter((field) => writableByField[field]));
    const values = fields
      .filter((field) => editableFields.has(field) && String(editDraft[field] ?? '') !== String(saved[field] ?? ''))
      .map((field) => ({ key: field, value: editDraft[field] ?? '' }));

    setEditLoading(true);
    try {
      const identifier = odrparticipant || participantId;
      if (values.length > 0) {
        const response = await updateReport({ variables: { identifier, id: editRow.id, values } });
        if (!response.data?.updateParticipantProgressReport?.success) throw new Error('The progress report was not updated.');
      }
      if (editDraft.completed !== '1') {
        await syncViewers({ variables: { reportId: editRow.id, viewerIds: viewers.map((viewer) => viewer.id) } });
      }
      toast.success(values.length ? 'Progress report updated.' : (editDraft.completed === '1' ? 'No changes to save.' : 'Progress report viewers saved.'));
      setEditOpen(false);
      setEditRow(null);
      await refetch();
    } catch (editError: any) {
      toast.error(editError?.message || 'Failed to update progress report.');
    } finally {
      setEditLoading(false);
    }
  };

  const submitCreate = async () => {
    const missing = PROGRESS_REPORT_REQUIRED_FIELDS.filter((field) => !String(draft[field] || '').trim());
    if (missing.length > 0) {
      toast.error(`Complete the required fields: ${missing.map((field) => labelsByField[field] || field).join(', ')}`);
      return;
    }

    setCreateLoading(true);
    try {
      const values = fields
        .filter((field) => schemaWritableByField[field] && !['participant_id', 'odrparticipant', 'cid', 'appearance'].includes(field) && String(draft[field] || '').trim())
        .map((field) => ({ key: field, value: draft[field] }));
      const response = await createReport({ variables: { participantId: lockedParticipantId, odrparticipant, cid: cid || null, values } });
      const created = response.data?.createParticipantProgressReport;
      if (!created?.success || !created.id) {
        throw new Error('The progress report was not created.');
      }
      try {
        await syncViewers({ variables: { reportId: created.id, viewerIds: viewers.map((viewer) => viewer.id) } });
      } catch (viewerError: any) {
        toast.error(viewerError?.message || 'The report was created, but its viewer list could not be saved.');
      }
      toast.success('Progress report created.');
      setCreateOpen(false);
      resetCreate();
      await refetch();
    } catch (createError: any) {
      toast.error(createError?.message || 'Failed to create progress report.');
    } finally {
      setCreateLoading(false);
    }
  };

  const saveColumnSettings = async () => {
    const nextColumns = pendingColumns.filter((field) => configurableFields.includes(field));
    try {
      const existing = (user?.settings?.systemSettingsFavorites || []).filter((value: string) => !value.startsWith(COLUMN_SETTING_PREFIX));
      await updateSystemSettingsFavorites([...existing, ...nextColumns.map((field) => `${COLUMN_SETTING_PREFIX}${field}`)]);
      setVisibleColumns(nextColumns.length > 0 ? nextColumns : DEFAULT_COLUMNS.filter((field) => configurableFields.includes(field)));
      setColumnConfigOpen(false);
      toast.success('Progress report columns saved.');
    } catch (settingsError: any) {
      toast.error(settingsError?.message || 'Unable to save progress report columns.');
    }
  };

  const columns = useMemo<UnifiedTableColumn<ReportRow>[]>(() => {
    const selected = visibleColumns.filter((field) => fields.includes(field));
    const dataColumns: UnifiedTableColumn<ReportRow>[] = selected.map((field) => ({
      key: field,
      header: labelsByField[field] || field,
      sortable: true,
      accessor: (row) => {
        const value = getCellValue(row, field);
        if (field === 'completed' || field === 'amended') {
          return value === '1' ? 'Yes' : 'No';
        }
        return value || '—';
      },
      sortValue: (row) => getCellValue(row, field),
    }));
    dataColumns.push({
      key: '__actions',
      header: 'Actions',
      accessor: (row) => (
        <div className="flex gap-1.5">
        <Button type="button" variant="outline" size="sm" className="h-7" onClick={() => {
          const values = row.cells.reduce<Record<string, string>>((record, cell) => {
            record[cell.key] = cell.value === null || cell.value === undefined ? '' : String(cell.value);
            return record;
          }, {});
          setPreviewValues(values);
          setPreviewOpen(true);
        }}>Preview</Button>
        <Button type="button" variant="outline" size="sm" className="h-7 gap-1" onClick={() => void openEdit(row)}><Pencil className="h-3.5 w-3.5" />Edit</Button>
        </div>
      ),
    });
    return dataColumns;
  }, [fields, labelsByField, visibleColumns]);

  return (
    <div className="flex h-full flex-col">
      <div className="px-4 pt-4">
        <TabHeader
          icon={FileText}
          title="Progress Reports"
          subtitle={`${rows.length} report${rows.length === 1 ? '' : 's'} for this participant`}
          action={<Button size="sm" onClick={() => void openCreate()}>New Progress Report</Button>}
        />
      </div>
      <div className="participant-card-toolbar flex items-center justify-between px-3 py-2">
        <span className="text-xs text-[var(--neutral-gray-80)]">Only reports linked to this participant are shown.</span>
        <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5" onClick={() => {
          setPendingColumns(visibleColumns);
          setColumnConfigOpen(true);
        }}><Settings2 className="h-3.5 w-3.5" />Columns</Button>
      </div>
      <div className="min-h-0 flex-1 px-4 pb-4 pt-3">
        <UnifiedTable columns={columns} data={rows} rowKey={(row) => String(row.id)} loading={loading} emptyTitle="No progress reports" emptyDescription="Create the first progress report for this participant." />
        {error && <div className="mt-3 text-sm text-[var(--semantic-error)]">Unable to load progress reports: {error.message}</div>}
      </div>

      <Dialog open={createOpen} onOpenChange={(open: boolean) => {
        if (!open && !createLoading) {
          resetCreate();
        }
        setCreateOpen(open);
      }}>
        <DialogContent className="flex flex-col overflow-hidden p-0" contentWidth="80vw" contentHeight="80vh" contentMaxWidth="80vw" contentMaxHeight="80vh">
          <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
            <DialogTitle className="text-left">New Progress Report · {clientName || `Participant ${odrparticipant}`}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {createLoading && Object.keys(draft).length === 0 ? <div className="py-12 text-center text-sm">Loading participant defaults...</div> : (
              <>
                <ProgressReportFields fields={fields} labelsByField={labelsByField} values={draft} writableByField={writableByField} onChange={(field, value) => setDraft((current) => ({ ...current, [field]: value }))} createMode />
                <ProgressReportViewerPicker selected={viewers} results={viewerResults} search={viewerSearch} onSearchChange={setViewerSearch} onAdd={addViewer} onRemove={(id) => setViewers((current) => current.filter((viewer) => viewer.id !== id))} />
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t border-[var(--neutral-gray-40)] px-5 py-3">
            <Button type="button" variant="outline" size="sm" onClick={() => { setPreviewValues(draft); setPreviewOpen(true); }} disabled={createLoading}>Preview</Button>
            <Button type="button" size="sm" onClick={() => void submitCreate()} disabled={createLoading}>{createLoading ? 'Saving...' : 'Create'}</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setCreateOpen(false)} disabled={createLoading}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={(open: boolean) => {
        if (!editLoading) setEditOpen(open);
        if (!open && !editLoading) setEditRow(null);
      }}>
        <DialogContent className="flex flex-col overflow-hidden p-0" contentWidth="80vw" contentHeight="80vh" contentMaxWidth="80vw" contentMaxHeight="80vh">
          <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
            <DialogTitle className="text-left">Edit Progress Report · {editDraft.client_name || clientName || `Participant ${odrparticipant}`}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <ProgressReportFields
              fields={fields}
              labelsByField={labelsByField}
              values={editDraft}
              writableByField={editDraft.completed === '1'
                ? Object.fromEntries(fields.map((field) => [field, field === 'client_current_condition' || field === 'appearance']))
                : writableByField}
              onChange={(field, value) => setEditDraft((current) => ({ ...current, [field]: value }))}
            />
            <ProgressReportViewerPicker selected={viewers} results={viewerResults} search={viewerSearch} onSearchChange={setViewerSearch} onAdd={addViewer} onRemove={(id) => setViewers((current) => current.filter((viewer) => viewer.id !== id))} disabled={editDraft.completed === '1'} />
          </div>
          <div className="flex justify-end gap-2 border-t border-[var(--neutral-gray-40)] px-5 py-3">
            <Button type="button" variant="outline" size="sm" onClick={() => { setPreviewValues(editDraft); setPreviewOpen(true); }} disabled={editLoading}>Preview</Button>
            <Button type="button" size="sm" onClick={() => void submitEdit()} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save Changes'}</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setEditOpen(false)} disabled={editLoading}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={columnConfigOpen} onOpenChange={setColumnConfigOpen}>
        <DialogContent className="flex max-h-[70vh] flex-col overflow-hidden p-0" contentWidth="60vw" contentMaxWidth="60vw" contentMaxHeight="70vh">
          <DialogHeader className="border-b border-[var(--neutral-gray-30)] px-4 py-3"><DialogTitle className="text-left">Configure Progress Report Columns</DialogTitle></DialogHeader>
          <div className="grid min-h-0 grid-cols-2 gap-x-2 gap-y-0 overflow-y-auto px-3 py-2 md:grid-cols-3 xl:grid-cols-4">
            {configurableFields.map((field) => (
              <label key={field} className="flex min-w-0 items-center gap-1.5 border-0 p-0 text-sm leading-6 shadow-none">
                <Checkbox checked={pendingColumns.includes(field)} onCheckedChange={() => setPendingColumns((current) => current.includes(field) ? current.filter((item) => item !== field) : [...current, field])} />
                <span className="truncate">{labelsByField[field] || field}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 border-t border-[var(--neutral-gray-30)] px-4 py-2.5">
            <Button size="sm" onClick={() => void saveColumnSettings()}>Save</Button>
            <Button variant="outline" size="sm" onClick={() => setColumnConfigOpen(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ProgressReportPreview open={previewOpen} values={previewValues} onOpenChange={setPreviewOpen} />
    </div>
  );
};
